"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { useAnalytics } from "@/hooks/useAnalytics";
import { analyticsFailureReason } from "@/lib/analyticsFailureReason";
import { ensureGuestSession } from "@/lib/ensure-guest-session";
import { recordPriceAlertCreated } from "@/lib/recordPriceAlertCreated";
import { getListsItemsQueryOptions } from "@/routes/_app/lists/lists-items-query";
import { client, orpc } from "@/utils/orpc.client";

type ReleaseWishlistMutationInput =
  | { listId?: string; operation: "add"; releaseId: string }
  | { listId: string; operation: "remove"; releaseId: string };

export function useReleaseWishlistToggle({
  wishlistListId,
  workId,
}: {
  wishlistListId: string | undefined;
  workId: string;
}) {
  const t = useTranslations("globals.lists");
  const queryClient = useQueryClient();
  const { scope, track } = useAnalytics();
  const mutation = useMutation({
    mutationFn: async (input: ReleaseWishlistMutationInput) => {
      await ensureGuestSession(queryClient);
      if (input.operation === "remove") {
        await client.lists.removeItem({ listId: input.listId, releaseId: input.releaseId });
      } else {
        const listsData = input.listId ? null : await client.lists.getListsItems();
        const resolvedWishlistListId =
          input.listId ?? listsData?.lists.find((list) => list.type === "wishlist")?.id;
        if (resolvedWishlistListId) {
          const wishlistItems = await client.lists.checkWishlistOverlap({ workId });
          await client.lists.updateItemLists({
            releaseId: input.releaseId,
            workId,
            toAdd: [resolvedWishlistListId],
            toRemove: [],
            wishlistOverlapReleaseIds: wishlistItems.map((item) => item.releaseId),
          });
        } else {
          await client.lists.addWishlistItems({ releaseIds: [input.releaseId] });
        }
      }
      return input.operation;
    },
    onSuccess: async (operation) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.featurePages.getRenderModel.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.lists.key() }),
        queryClient.invalidateQueries({ queryKey: getListsItemsQueryOptions().queryKey }),
      ]);
      if (operation === "add") {
        track({ name: "wishlist_item_added", properties: { item_count: 1 } });
        recordPriceAlertCreated({ selectionScope: "single", surface: scope.surface });
      }
      toast.success(operation === "add" ? t("addedToLists") : t("updatedLists"));
    },
    onError: (error: Error, input) => {
      if (input.operation === "add") {
        track({
          name: "wishlist_add_failed",
          properties: { failure_reason: analyticsFailureReason(error) },
        });
      }
      toast.error(error.message);
    },
  });

  const toggle = useCallback(
    ({ inWishlist, releaseId }: { inWishlist: boolean; releaseId: string }) => {
      if (inWishlist && wishlistListId) {
        mutation.mutate({ listId: wishlistListId, operation: "remove", releaseId });
        return;
      }
      track({ name: "wishlist_add_started", properties: {} });
      mutation.mutate({ listId: wishlistListId, operation: "add", releaseId });
    },
    [mutation, track, wishlistListId],
  );

  return { isPending: mutation.isPending, toggle };
}
